/**
 * Event Logger
 *
 * Centralized event logging for analytics, audit trail, and achievement tracking.
 * Used across all frontend applications for consistent event tracking.
 *
 * Features:
 * - Local storage persistence with automatic cleanup
 * - Subscriber pattern for reactive event handling
 * - Query methods for filtering events
 * - Visibility controls for privacy
 * - Platform-agnostic storage interface
 */

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Event types that can be logged across the platform
 */
export type EventType =
  // CheckUp events
  | 'CHECKUP_STARTED'
  | 'CHECKUP_COMPLETED'
  | 'CHECKUP_SECTION_COMPLETED'
  // QultureUp events
  | 'QULTUREUP_LESSON_STARTED'
  | 'QULTUREUP_LESSON_COMPLETED'
  | 'QULTUREUP_MISSION_STARTED'
  | 'QULTUREUP_MISSION_COMPLETED'
  | 'QULTUREUP_WORLD_COMPLETED'
  // TelePortal events
  | 'TELEPORTAL_TASK_STARTED'
  | 'TELEPORTAL_TASK_COMPLETED'
  | 'TELEPORTAL_DOCUMENT_UPLOADED'
  // Care events
  | 'CARE_CHECKIN_COMPLETED'
  | 'CARE_SELF_FEEDBACK_COMPLETED'
  | 'CARE_MANAGER_FEEDBACK_COMPLETED'
  | 'CARE_SUPPORT_REQUESTED'
  // Passport events
  | 'PASSPORT_STAMP_EARNED'
  | 'PASSPORT_LEVEL_UP'
  // Journey events
  | 'JOURNEY_MILESTONE_REACHED'
  | 'JOURNEY_CREATED'
  | 'JOURNEY_COMPLETED'
  // User events
  | 'LOGIN'
  | 'LOGOUT'
  | 'PROFILE_UPDATED'
  | 'DOCUMENT_UPLOADED'
  // Achievement events
  | 'ACHIEVEMENT_UNLOCKED'
  | 'HERO_ASSIGNED'
  // CoPilot events
  | 'COPILOT_SESSION'
  | 'COPILOT_MESSAGE_SENT'
  // Business events
  | 'VETTING_COMPLETED'
  | 'TALENT_INVITED'
  | 'COHORT_CREATED'
  // Generic
  | 'FEEDBACK_SUBMITTED'
  | 'MISSION_COMPLETED'
  | 'CUSTOM';

/**
 * Event visibility controls - who can see this event
 */
export interface EventVisibility {
  /** Visible to the talent themselves */
  talent: boolean;
  /** Visible to sponsoring company (aggregated) */
  company: boolean;
  /** Visible to Tonnie platform admins */
  tonnie: boolean;
}

/**
 * Event payload data
 */
export interface EventData {
  [key: string]: unknown;
  score?: number;
  category?: string;
  achievementId?: string;
  duration?: number;
  source?: string;
}

/**
 * Complete event record
 */
export interface ReadinessEvent {
  id: string;
  userId: string;
  type: EventType;
  category: string;
  data: EventData;
  timestamp: string;
  visibility: EventVisibility;
  journeyInstanceId?: string;
}

/**
 * Event callback for subscribers
 */
export type EventCallback = (event: ReadinessEvent) => void;

/**
 * Storage interface for platform-agnostic persistence
 */
export interface EventStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

// ═══════════════════════════════════════════════════════════════════════
// DEFAULT STORAGE
// ═══════════════════════════════════════════════════════════════════════

/**
 * Browser localStorage implementation
 */
export const browserStorage: EventStorage = {
  getItem: (key: string) => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
  },
};

/**
 * In-memory storage for SSR or testing
 */
const memoryData: Record<string, string> = {};

export const memoryStorage: EventStorage = {
  getItem(key: string) {
    return memoryData[key] || null;
  },
  setItem(key: string, value: string) {
    memoryData[key] = value;
  },
  removeItem(key: string) {
    delete memoryData[key];
  },
};

// ═══════════════════════════════════════════════════════════════════════
// EVENT LOGGER CLASS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Configuration options for EventLogger
 */
export interface EventLoggerConfig {
  /** Storage implementation (defaults to browserStorage) */
  storage?: EventStorage;
  /** Storage key prefix */
  storageKey?: string;
  /** Maximum events to retain */
  maxEvents?: number;
  /** Enable console logging in development */
  debug?: boolean;
}

/**
 * Event Logger Service
 *
 * Centralized event logging with persistence and subscription support.
 *
 * @example
 * ```typescript
 * import { createEventLogger } from '@tonnie/foundation';
 *
 * const eventLogger = createEventLogger();
 *
 * // Log an event
 * eventLogger.log('user_123', 'CHECKUP_COMPLETED', 'CHECKUP', {
 *   score: 85,
 *   duration: 1200,
 * });
 *
 * // Subscribe to events
 * const unsubscribe = eventLogger.subscribe((event) => {
 *   console.log('New event:', event);
 * });
 *
 * // Get events for a user
 * const events = eventLogger.getEvents('user_123');
 * ```
 */
export class EventLogger {
  private events: ReadinessEvent[] = [];
  private subscribers: EventCallback[] = [];
  private storage: EventStorage;
  private storageKey: string;
  private maxEvents: number;
  private debug: boolean;
  private initialized = false;

  constructor(config: EventLoggerConfig = {}) {
    this.storage = config.storage || browserStorage;
    this.storageKey = config.storageKey || 'tonnie_events';
    this.maxEvents = config.maxEvents || 1000;
    this.debug = config.debug ?? process.env['NODE_ENV'] === 'development';
  }

  /**
   * Initialize the logger by loading persisted events
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    const stored = await this.storage.getItem(this.storageKey);
    if (stored) {
      try {
        this.events = JSON.parse(stored);
      } catch (e) {
        console.error('[EventLogger] Failed to load events:', e);
        this.events = [];
      }
    }
    this.initialized = true;
  }

  /**
   * Save events to storage
   */
  private async saveEvents(): Promise<void> {
    const eventsToStore = this.events.slice(-this.maxEvents);
    await this.storage.setItem(this.storageKey, JSON.stringify(eventsToStore));
  }

  /**
   * Generate a unique event ID
   */
  private generateId(): string {
    return `evt_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Log an event
   */
  async log(
    userId: string,
    eventType: EventType,
    category: string,
    data: EventData = {},
    visibility: EventVisibility = { talent: true, company: false, tonnie: true },
    journeyInstanceId?: string
  ): Promise<ReadinessEvent> {
    if (!this.initialized) {
      await this.init();
    }

    const event: ReadinessEvent = {
      id: this.generateId(),
      userId,
      type: eventType,
      category,
      data,
      timestamp: new Date().toISOString(),
      visibility,
      journeyInstanceId,
    };

    this.events.push(event);
    await this.saveEvents();

    // Notify subscribers
    this.subscribers.forEach((callback) => {
      try {
        callback(event);
      } catch (e) {
        console.error('[EventLogger] Subscriber error:', e);
      }
    });

    // Debug logging
    if (this.debug) {
      console.log('[EVENT]', eventType, category, data);
    }

    return event;
  }

  /**
   * Get all events for a user
   */
  getEvents(userId: string): ReadinessEvent[] {
    return this.events.filter((e) => e.userId === userId);
  }

  /**
   * Get events by type for a user
   */
  getEventsByType(userId: string, type: EventType): ReadinessEvent[] {
    return this.events.filter((e) => e.userId === userId && e.type === type);
  }

  /**
   * Get events by category for a user
   */
  getEventsByCategory(userId: string, category: string): ReadinessEvent[] {
    return this.events.filter((e) => e.userId === userId && e.category === category);
  }

  /**
   * Get events within a date range
   */
  getEventsInDateRange(userId: string, startDate: Date, endDate: Date): ReadinessEvent[] {
    return this.events.filter((e) => {
      if (e.userId !== userId) return false;
      const eventDate = new Date(e.timestamp);
      return eventDate >= startDate && eventDate <= endDate;
    });
  }

  /**
   * Subscribe to new events
   */
  subscribe(callback: EventCallback): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  /**
   * Clear all events for a user
   */
  async clearEvents(userId: string): Promise<void> {
    this.events = this.events.filter((e) => e.userId !== userId);
    await this.saveEvents();
  }

  /**
   * Clear all events
   */
  async clearAllEvents(): Promise<void> {
    this.events = [];
    await this.storage.removeItem(this.storageKey);
  }

  /**
   * Get event count for a user
   */
  getEventCount(userId: string): number {
    return this.events.filter((e) => e.userId === userId).length;
  }

  /**
   * Get the latest event for a user
   */
  getLatestEvent(userId: string): ReadinessEvent | undefined {
    const userEvents = this.events.filter((e) => e.userId === userId);
    return userEvents[userEvents.length - 1];
  }
}

// ═══════════════════════════════════════════════════════════════════════
// FACTORY FUNCTION
// ═══════════════════════════════════════════════════════════════════════

/**
 * Create a new EventLogger instance
 */
export function createEventLogger(config?: EventLoggerConfig): EventLogger {
  return new EventLogger(config);
}

/**
 * Default singleton instance for convenience
 */
export const eventLogger = createEventLogger();
